import { supabase } from "./supabase";
import type { ClockReading, TimecodeSpec } from "@infoflyt/shared/timecode";
import { aapneOekt, ankerFor, finnOekt, naa, type Anker } from "./nettlogg";

/*
 * ANKERET KOMMER FRA `nettlogg.ts`, det lages ikke her.
 *
 * Fram til 27.10.2026 sto en ordrett kopi i denne fila: egen `BOOT`, egen
 * `naa()`, egen `jamFor` og egen `ankerFor`. To kopier av tidskodelogikk i
 * samme fane er ikke bare dobbelt arbeid — de hadde hver sin `BOOT`, og da
 * sa to ankere fra samme nettleser at de kom fra hver sin klokke.
 *
 * `jamFor`, `naa` og `Anker` sendes videre herfra fordi flatene leser dem
 * som `synk.jamFor` og `synk.naa`. Det er samme funksjon, ikke en ny.
 */
export { jamFor, naa, type Anker } from "./nettlogg";

/**
 * Reporterens synk: start, slutt, hake og svar.
 *
 * En REPORTER har synkene som hovedansvar og sitter ofte med PC. Mange gjør
 * synken som en SAMTALE, og da er det ikke alltid mulig å logge mens man
 * snakker — men noen ganger er det det. Derfor må begge veier virke: huke av
 * underveis, eller gå gjennom lista etterpå. Avgjort 24.10.2026.
 *
 * ========  DETTE ER DEN FØRSTE FLATA SOM SKRIVER TIL LOGGEN  ========
 *
 * `CLAUDE.md` sier at loggen går én vei: opp. Regelen sto som «en loggpost
 * slettes bare i appen», og underforstått at opphavet alltid er en telefon.
 * Den er presisert 24.10.2026: regelen gjelder per logg og dens eget opphav.
 * Reporterens logg OPPSTÅR her, så det finnes ingen telefonkopi å komme i
 * utakt med.
 *
 * Det som ikke er endret: INGEN SKRIVER I EN ANNEN MANNS LOGG. Alt her
 * skriver bare til reporterens egen økt, og aldri til en post noen andre har
 * skrevet. Redaktørens flate har fortsatt ingen skrivevei til `log_entry`.
 *
 * ========  TIDSKODEN ER EKTE, OG DEN SIER AT DEN ER USIKKER  ========
 *
 * Ankeret hører til (produksjon, opptaksdag), så har en regissør alt satt
 * dagens tidskode mot boksen, er det DEN reporteren logger mot — og da
 * stemmer synkens tidskode med materialet. Det er hele poenget med å ha den
 * i loggen.
 *
 * Men en PC er ikke jammet mot noe. `timecodeAt` ser at `boot_id` er en
 * annen enn ankerets, regner forløpt tid fra veggklokka og setter
 * `state: "usikker"`. Det er riktig svar og ikke en nødløsning: reporterens
 * tidskode er like god som PC-klokka, og flata skal si det framfor å late
 * som noe annet. Se `docs/04-timecode.md`.
 */

export interface Synkpost {
  readonly id: string;
  readonly deltakerId: string | null;
  readonly label: string;
  readonly tekst: string;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
}

export interface Svar {
  readonly id: string;
  readonly postId: string;
  readonly spoersmaalId: string;
  readonly deltakerId: string;
  readonly svar: string;
}

export interface Synkdata {
  readonly anker: Anker | null;
  readonly oektId: string | null;
  readonly poster: readonly Synkpost[];
  readonly svar: readonly Svar[];
}

export const TOM: Synkdata = { anker: null, oektId: null, poster: [], svar: [] };

/** Etikettene. Markøren bærer dem, ikke knappen — se migrasjon 20261021090000. */
export const START = "SYNK";
export const SLUTT = "SYNK SLUTT";

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function tilPost(r: Record<string, unknown>): Synkpost {
  return {
    id: str(r["id"]),
    deltakerId: r["participant_id"] === null || r["participant_id"] === undefined
      ? null : str(r["participant_id"]),
    label: str(r["label"]),
    tekst: str(r["text"]),
    monotonicMs: Number(r["monotonic_ms"]),
    bootId: str(r["boot_id"]),
    wallUtcMs: Date.parse(str(r["wall_utc"])),
  };
}

/*
 * OEKTA KOMMER FRA `nettlogg.ts`, den slaas ikke opp her.
 *
 * Her sto en ordrett kopi av `finnOekt` og `aapneOekt` fram til
 * 29.10.2026 — samme duplikat som ankeret hadde, og det kostet i samme
 * oeyeblikk: da teamet begynte aa dele logg, maatte regelen inn to steder,
 * og den ene ville blitt glemt.
 *
 * Kommentaren som sto her og er verdt aa ta med: oekta LAGES bare naar noe
 * skal skrives. `hent` leser bare. Aapnet den en logg, la en reporter som
 * bare kikket igjen en tom spalte paa redaktoerens loggflate.
 */

export interface Hvem {
  readonly produksjonId: string;
  readonly opptaksdag: string;
  readonly medlemId: string;
  readonly framerate: string;
  readonly dropFrame: boolean;
  /**
   * Teamet, eller null.
   *
   * ========  SYNKEN OG LIVELOGGEN MAA DELE OEKT  ========
   *
   * Her sto ingenting, og `finnOekt` ble kalt uten team — synken skrev
   * derfor i ens EGEN oekt mens liveloggen skrev i teamets. To oekter paa
   * samme dag, og `report.session_id` er én fremmednoekkel: da kan ikke
   * «liveloggen og synken blir til en rapport» vaere sant. Lagt til
   * 02.11.2026, da dagen skulle kunne avsluttes fra nettflata.
   *
   * For en reporter er den som regel null — hen jobber alene — og da er
   * det ingen endring: oekta slaas opp paa forfatter som foer.
   */
  readonly teamId?: string | null;
}

/**
 * Alt flata trenger: anker, egen økt, egne synkposter og svarene.
 *
 * Kaster ikke. Uten klient eller uten nett er flata tom, og det er en
 * tilstand skjermen må kunne vise.
 */
export async function hent(hvem: Hvem, spec: TimecodeSpec): Promise<Synkdata> {
  if (supabase === null) return TOM;

  const anker = await ankerFor(
    hvem.produksjonId, hvem.opptaksdag, hvem.medlemId, spec, hvem.framerate, hvem.dropFrame,
  );
  if (anker === null) return TOM;
  /* LESER BARE. Oekta lages naar synken startes — se `aapneOekt`. */
  const oektId = await finnOekt(
    hvem.produksjonId, hvem.opptaksdag, hvem.medlemId, hvem.teamId ?? null);
  if (oektId === null) return { ...TOM, anker };

  const [p, s] = await Promise.all([
    supabase
      .from("log_entry")
      .select("id, participant_id, label, text, monotonic_ms, boot_id, wall_utc")
      .eq("session_id", oektId)
      .is("deleted_at", null)
      .in("label", [START, SLUTT])
      .order("wall_utc", { ascending: true }),
    /*
     * ALLE svarene i produksjonen, ikke bare mine.
     *
     * ET SPOERSMAAL SOM ER STILT ER STILT FOR ALLE. Avgjort 25.10.2026. Har
     * regi spurt ALI om noe fra appen, skal reporteren se at det er gjort —
     * ellers faar deltakeren samme spoersmaal to ganger, og det er nettopp
     * det lista finnes for aa hindre.
     *
     * `!inner` paa posten med `deleted_at is null` holder invarianten: et
     * svar teller bare naar POSTEN LEVER. Slettes synkposten, faller
     * spoersmaalet tilbake til ustilt — ogsaa her. Se migrasjon
     * 20261011140000.
     */
    supabase
      .from("su_answer")
      .select("id, entry_id, question_id, participant_id, answer, post:log_entry!inner(deleted_at)")
      .eq("production_id", hvem.produksjonId)
      .is("post.deleted_at", null),
  ]);
  if (p.error !== null || s.error !== null) return { ...TOM, anker, oektId };

  return {
    anker,
    oektId,
    poster: (p.data ?? []).map((r) => tilPost(r as unknown as Record<string, unknown>)),
    svar: (s.data ?? []).map((r) => {
      const x = r as unknown as Record<string, unknown>;
      return {
        id: str(x["id"]),
        postId: str(x["entry_id"]),
        spoersmaalId: str(x["question_id"]),
        deltakerId: str(x["participant_id"]),
        svar: str(x["answer"]),
      };
    }),
  };
}

export interface Rammer {
  /** Oekta, naar den finnes. Null til foerste synk startes. */
  readonly oektId: string | null;
  readonly produksjonId: string;
  readonly ankerId: string;
  /**
   * Boot-id-en ankeret ble satt med.
   *
   * Avgjør om posten er `ok` eller `usikker`. Satte en regissør ankeret mot
   * boksen fra telefonen sin, er den aldri lik vår — og det er riktig: vi
   * regner fra veggklokka da.
   */
  readonly ankerBootId: string;
  readonly medlemId: string;
  /** Trengs for aa kunne aapne oekta ved foerste start. */
  readonly opptaksdag: string;
  /** Teamet, eller null. Avgjoer hvilken oekt synkposten havner i. */
  readonly teamId?: string | null;
}

/**
 * Setter en markør i reporterens egen logg.
 *
 * Tidskoden FANGES VED TRYKKET, ikke ved lagringen: avlesningen tas her, og
 * den følger posten til basen. Går nettet, er det avlesningen fra trykket som
 * står — ikke tidspunktet svaret kom fram.
 */
async function markoer(
  r: Rammer, label: string, tekst: string, deltakerId: string | null, lest: ClockReading,
): Promise<Synkpost | null> {
  if (supabase === null) return null;
  /* Foerste markoer aapner oekta. Ingen logg oppstaar av aa se paa flata. */
  const oektId = r.oektId
    ?? await aapneOekt(r.produksjonId, r.opptaksdag, r.medlemId, r.ankerId,
                       r.teamId ?? null);
  if (oektId === null) return null;
  const rad = {
    id: crypto.randomUUID(),
    session_id: oektId,
    production_id: r.produksjonId,
    anchor_id: r.ankerId,
    author_id: r.medlemId,
    label,
    /* Token og ikke fast verdi, så stripa følger paletten i blackout. Samme
       farge som appen gir SYNK-posten. */
    color: "var(--tc)",
    text: tekst,
    monotonic_ms: lest.monotonicMs,
    boot_id: lest.bootId,
    wall_utc: new Date(lest.wallUtcMs).toISOString(),
    /* Satte en ANNEN enhet ankeret, er tidskoden regnet fra veggklokka og
       dermed usikker — og posten skal si det. `timecodeAt` kommer til samme
       svar av seg selv, men feltet er det klipperen leser. */
    tc_state: lest.bootId === r.ankerBootId ? "ok" : "usikker",
    participant_id: deltakerId,
    /* Posten ble til HER. Se migrasjon 20261102210000. */
    origin: "web",
  };
  const { error } = await supabase.from("log_entry").insert(rad);
  if (error !== null) return null;
  return {
    id: rad.id,
    deltakerId,
    label,
    tekst,
    monotonicMs: lest.monotonicMs,
    bootId: lest.bootId,
    wallUtcMs: lest.wallUtcMs,
  };
}

/** «Start synk». Markøren bærer deltakerens forkortelse, som i appen. */
export function startSynk(
  r: Rammer, deltakerId: string, forkortelse: string, lest: ClockReading,
): Promise<Synkpost | null> {
  return markoer(r, START, forkortelse, deltakerId, lest);
}

/** «Ferdig med synk». Egen markør, fordi slutten er en egen hendelse. */
export function avsluttSynk(
  r: Rammer, deltakerId: string, forkortelse: string, lest: ClockReading,
): Promise<Synkpost | null> {
  return markoer(r, SLUTT, forkortelse, deltakerId, lest);
}

/**
 * Huker av et spørsmål på synken.
 *
 * HAKEN ER IKKE EN TILSTAND. Et spørsmål er stilt hvis det finnes et svar og
 * SYNK-POSTEN LEVER — ikke fordi en `answered`-kolonne står et sted. Da går
 * koblingen begge veier av seg selv. Se migrasjon 20261011140000.
 */
export async function hukAv(
  produksjonId: string, postId: string, spoersmaalId: string, deltakerId: string,
): Promise<Svar | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase
    .from("su_answer")
    .insert({
      production_id: produksjonId,
      entry_id: postId,
      question_id: spoersmaalId,
      participant_id: deltakerId,
    })
    .select("id, entry_id, question_id, participant_id, answer")
    .single();
  if (error !== null || data === null) return null;
  const x = data as unknown as Record<string, unknown>;
  return {
    id: str(x["id"]),
    postId: str(x["entry_id"]),
    spoersmaalId: str(x["question_id"]),
    deltakerId: str(x["participant_id"]),
    svar: str(x["answer"]),
  };
}

/**
 * Skriver synken inn i loggposten.
 *
 * ========  POSTEN ER DET REDAKTOEREN LESER  ========
 *
 * Avhukingen ligger i `su_answer`, og svaret med den. Men `su_answer` er en
 * tabell ingen leser i loggen — redaktoerens flate viser `log_entry.text`, og
 * der sto det bare forkortelsen. Da saa en hel synk ut som en tom markoer.
 * Rettet 25.10.2026.
 *
 * BYGGES OPP PAA NYTT HVER GANG, ikke lagt til paa. Appen legger til, fordi
 * regi kan ha skrevet egen tekst inn i synkposten som ikke skal viskes ut.
 * Her skriver ingen i posten direkte, og da er en ombygging det eneste som
 * holder teksten riktig naar en hake angres eller et svar rettes.
 *
 * Formen er appens: forkortelsen paa foerste linje, saa ett spoersmaal per
 * linje. Svaret staar under sitt spoersmaal med tankestrek, slik at det
 * leses som svaret og ikke som neste spoersmaal.
 */
export async function skrivInnIPosten(
  postId: string,
  forkortelse: string,
  linjer: readonly { readonly spoersmaal: string; readonly svar: string }[],
): Promise<boolean> {
  if (supabase === null) return false;
  const tekst = [
    forkortelse,
    ...linjer.flatMap((l) => (l.svar === "" ? [l.spoersmaal] : [l.spoersmaal, `– ${l.svar}`])),
  ].join("\n");
  const { error } = await supabase.from("log_entry").update({ text: tekst }).eq("id", postId);
  return error === null;
}

/**
 * Trekker en synk tilbake.
 *
 * ET FEILTRYKK SKAL IKKE LEGGE IGJEN EN TIDSKODE. Trykker man «Start synk»
 * og «Ferdig» rett etter uten å ha huket av noe, er det stort sett noen som
 * kom borti knappen — og en markør uten innhold i loggen er verre enn
 * ingen: klipperen leter etter noe som aldri skjedde. Bestilt 25.10.2026.
 *
 * TOMBSTONE, IKKE SLETTING. Loggen er append-only, og det gjelder også her.
 * Posten blir stående med `deleted_at`, og flatene filtrerer den bort. Se
 * CLAUDE.md.
 *
 * Dette er loggens EGEN flate som trekker tilbake sin EGEN post — ingen
 * rører noe de ikke har skrevet.
 */
export async function trekkTilbake(postId: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase
    .from("log_entry")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", postId);
  return error === null;
}

/** Tar haken bort igjen. Spørsmålet blir ustilt, og står igjen på lista. */
export async function angreHuk(id: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("su_answer").delete().eq("id", id);
  return error === null;
}

/**
 * Skriver ned hva de svarte.
 *
 * Kan skrives, rettes og skrives om. Det er forskjellen fra loggteksten, som
 * er append-only: et svar man noterer i ettertid er noe man strammer inn
 * etter å ha hørt gjennom opptaket. Se migrasjonen 20261024090000.
 */
export async function skrivSvar(
  id: string, tekst: string, medlemId: string,
): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase
    .from("su_answer")
    .update({
      answer: tekst,
      answer_at: new Date().toISOString(),
      answer_by: medlemId,
    })
    .eq("id", id);
  return error === null;
}
